import React, { useEffect, useState, useRef, useCallback } from 'react';
import api from '../config/axios';
import * as XLSX from 'xlsx';

const Sales: React.FC = () => {
  const [sellers, setSellers] = useState<{email:string,name:string,segment:string}[]>([]);
  const [teams, setTeams] = useState<{id:string,name:string,segment:string}[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [segments, setSegments] = useState<{code:string,document_type:string}[]>([]);
  const [rows, setRows] = useState<any[]>([]);
  const [form, setForm] = useState<any>({ 
    segment: 'B2C', 
    teamId: '',
    sellerEmail: '',
    sellerName: '',
    status: 'ASIGNADO',
    incomeType: 'AGR',
    origin: 'Postpago',
    systemUser: '',
    cantidad: 1
  });
  const [loading, setLoading] = useState(false);
  const [sellerSearch, setSellerSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [showSellerDropdown, setShowSellerDropdown] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const sellerRef = useRef<HTMLDivElement>(null);
  const productRef = useRef<HTMLDivElement>(null);
  
  // Estados de loading para cada select
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [loadingSellers, setLoadingSellers] = useState(false);
  
  // Estados para modal de cliente
  const [showClientModal, setShowClientModal] = useState(false);
  const [clientForm, setClientForm] = useState<any>({
    document_type: 'DNI',
    dni_ruc: '',
    name: '',
    legal_representative: '',
    phone: '',
    address: '',
    department: '',
    province: '',
    district: ''
  });
  const [departments, setDepartments] = useState<string[]>([]);
  const [provinces, setProvinces] = useState<string[]>([]);
  const [districts, setDistricts] = useState<string[]>([]);
  
  // Estados para edición de ventas
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSale, setEditingSale] = useState<any>(null);
  const [editForm, setEditForm] = useState({
    productSku: '',
    status: '',
    fe: '',
    orderCode: '',
    serviceId: '',
    financialAccount: '',
    cantidad: 1
  });
  const [productSearchEdit, setProductSearchEdit] = useState('');
  const [showProductDropdownEdit, setShowProductDropdownEdit] = useState(false);
  const productEditRef = useRef<HTMLDivElement>(null);
  
  // Estados para logs de cambios
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [saleLogs, setSaleLogs] = useState<any[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [selectedSaleForLogs, setSelectedSaleForLogs] = useState<any>(null);
  
  // Estados para filtros y paginado
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [filteredRows, setFilteredRows] = useState<any[]>([]);

  useEffect(() => {
    // Cargar datos iniciales
    api.get('/products').then(r=> setProducts(r.data || [])).catch(()=>{});
    api.get('/segments').then(r=> setSegments(r.data || [])).catch(()=>{});
    api.get('/locations/departments').then(r=> setDepartments(r.data || [])).catch(()=>{});
    api.get('/sales', { params: { } }).then(r=> {
      const data = r.data || [];
      setRows(data);
    }).catch(()=>{});
    
    // Cargar equipos del segmento inicial
    loadTeamsForSegment('B2C');
  }, []);

  // Cargar equipos cuando cambia el segmento
  useEffect(() => {
    if (form.segment) {
      loadTeamsForSegment(form.segment);
    }
  }, [form.segment]);

  const loadSellersForTeam = useCallback(async (teamId: string) => {
    setLoadingSellers(true);
    try {
      // Obtener miembros del equipo
      const membersResponse = await api.get(`/teams/${teamId}/members`);
      const members = membersResponse.data || [];
      
      // Obtener usuarios para tener los nombres
      const usersResponse = await api.get('/users', { params: { role: 'vendedor' } });
      const allUsers = usersResponse.data || [];
      
      // Mapear miembros con nombres
      const sellersWithNames = members.map((member: any) => {
        const user = allUsers.find((u: any) => u.email === member.seller_email);
        return {
          email: member.seller_email,
          name: user ? user.name : member.seller_email,
          segment: form.segment
        };
      });
      
      setSellers(sellersWithNames);
      
      // Limpiar selección de vendedor
      setForm((prev: any) => ({...prev, sellerEmail: '', sellerName: ''}));
    } catch (error) {
      console.error('Error loading sellers for team:', error);
      setSellers([]);
    } finally {
      setLoadingSellers(false);
    }
  }, [form.segment]);

  // Cargar vendedores cuando cambia el equipo
  useEffect(() => {
    if (form.teamId) {
      loadSellersForTeam(form.teamId);
    } else {
      setSellers([]);
    }
  }, [form.teamId, loadSellersForTeam]);

  const loadTeamsForSegment = async (segment: string) => {
    setLoadingTeams(true);
    try {
      const teamsResponse = await api.get('/teams');
      const allTeams = teamsResponse.data || [];
      const segmentTeams = allTeams.filter((team: any) => team.segment === segment);
      setTeams(segmentTeams);
      
      // Limpiar selección de equipo y vendedor
      setForm((prev: any) => ({...prev, teamId: '', sellerEmail: '', sellerName: ''}));
      setSellers([]);
    } catch (error) {
      console.error('Error loading teams for segment:', error);
      setTeams([]);
    } finally {
      setLoadingTeams(false);
    }
  };

  // Función para obtener el texto dinámico según el segmento
  const getDocumentText = () => {
    const segmentInfo = segments.find(s => s.code === form.segment);
    const documentType = segmentInfo?.document_type || 'DNI';
    
    if (documentType === 'DNI') {
      return {
        label: 'DNI',
        placeholder: 'Ingrese DNI para buscar cliente',
        nameLabel: 'Nombre'
      };
    } else {
      return {
        label: 'RUC',
        placeholder: 'Ingrese RUC para buscar cliente',
        nameLabel: 'Razón Social'
      };
    }
  };

  // Actualizar tipo de documento cuando cambie el segmento
  useEffect(() => {
    if (segments.length > 0 && form.segment) {
      const segmentInfo = segments.find(s => s.code === form.segment);
      const newDocumentType = segmentInfo?.document_type || 'DNI';
      
      // Si el modal de cliente está abierto, actualizar el tipo de documento
      if (showClientModal && clientForm.document_type !== newDocumentType) {
        setClientForm((prev: any) => ({
          ...prev,
          document_type: newDocumentType,
          dni_ruc: '' // Limpiar el DNI/RUC cuando cambie el tipo
        }));
      }
    }
  }, [form.segment, segments, showClientModal, clientForm.document_type]);

  // Recalcular años/mes cuando cambian las filas
  useEffect(() => {
    const data = rows || [];
    // Calcular años disponibles
    const years = new Set<number>();
    data.forEach((sale: any) => {
      const year = new Date(sale.date).getFullYear();
      if (year >= 2025) years.add(year);
    });
    if (years.size === 0) years.add(new Date().getFullYear());
    const sortedYears = Array.from(years).sort((a, b) => b - a);
    setAvailableYears(sortedYears);

    // Seleccionar año/mes del registro más reciente si no coincide
    if (data.length > 0) {
      const latest = data.reduce((acc: any, cur: any) => new Date(cur.date) > new Date(acc.date) ? cur : acc, data[0]);
      const d = new Date(latest.date);
      if (!sortedYears.includes(selectedYear)) setSelectedYear(d.getFullYear());
      // Si el año activo no tiene ventas para el mes seleccionado, ajustar mes al del último registro
      if (selectedYear === d.getFullYear()) setSelectedMonth(d.getMonth() + 1);
    }
  }, [rows, selectedYear]);

  // Efecto para filtrar y ordenar datos
  useEffect(() => {
    let filtered = rows.filter(row => {
      const date = new Date(row.date);
      const matchesMonth = date.getMonth() + 1 === selectedMonth;
      const matchesYear = date.getFullYear() === selectedYear;
      
      if (!matchesMonth || !matchesYear) return false;
      
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          row.customer_name?.toLowerCase().includes(searchLower) ||
          row.customer_id?.toLowerCase().includes(searchLower) ||
          row.seller_email?.toLowerCase().includes(searchLower) ||
          row.product_sku?.toLowerCase().includes(searchLower) ||
          row.status?.toLowerCase().includes(searchLower)
        );
      }
      
      return true;
    });

    // Ordenar
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortField === 'date') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      } else if (sortField === 'amount') {
        aVal = Number(aVal);
        bVal = Number(bVal);
      } else {
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredRows(filtered);
    setCurrentPage(1); // Reset a la primera página cuando cambian los filtros
  }, [rows, selectedMonth, selectedYear, searchTerm, sortField, sortOrder]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sellerRef.current && !sellerRef.current.contains(event.target as Node)) {
        setShowSellerDropdown(false);
      }
      if (productRef.current && !productRef.current.contains(event.target as Node)) {
        setShowProductDropdown(false);
      }
      if (productEditRef.current && !productEditRef.current.contains(event.target as Node)) {
        setShowProductDropdownEdit(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); 
    
    // Validaciones
    if (!form.sellerEmail) {
      alert('Por favor seleccione un vendedor');
      return;
    }
    if (!form.teamId) {
      alert('Por favor seleccione un área');
      return;
    }
    if (!form.productSku) {
      alert('Por favor seleccione un producto');
      return;
    }
    if (!form.amount || Number(form.amount) <= 0) {
      alert('Por favor ingrese un monto válido');
      return;
    }
    if (!form.cantidad || Number(form.cantidad) <= 0) {
      alert('Por favor ingrese una cantidad válida');
      return;
    }
    if (!form.customerId || form.customerId.trim() === '') {
      alert('Por favor ingrese el DNI/RUC del cliente');
      return;
    }
    if (!form.customerName || form.customerName.trim() === '') {
      alert('Por favor ingrese el nombre o razón social del cliente');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        sellerEmail: form.sellerEmail,
        segment: form.segment,
        teamId: form.teamId,
        productSku: form.productSku,
        amount: Number(form.amount),
        cantidad: Number(form.cantidad),
        customerId: form.customerId.trim(),
        customerName: form.customerName.trim(),
        status: form.status,
        incomeType: form.incomeType,
        origin: form.origin,
        systemUser: form.systemUser || '',
        fe: form.fe || '',
        orderCode: form.orderCode || '',
        serviceId: form.serviceId || '',
        financialAccount: form.financialAccount || ''
      };
      await api.post('/sales', payload);
      const { data } = await api.get('/sales', { params: { } });
      setRows(data || []);
      setForm({ segment: 'B2C', status: 'ASIGNADO', incomeType: 'AGR', origin: 'Postpago', systemUser: '' });
      setSellerSearch('');
      setProductSearch('');
    } finally { setLoading(false); }
  };

  const filteredSellers = sellers.filter(s => 
    s.name.toLowerCase().includes(sellerSearch.toLowerCase()) || 
    s.email.toLowerCase().includes(sellerSearch.toLowerCase())
  );

  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(productSearch.toLowerCase()) || 
    p.sku.toLowerCase().includes(productSearch.toLowerCase())
  );

  // Funciones para manejar ubicaciones
  const handleDepartmentChange = async (department: string) => {
    setClientForm({...clientForm, department, province: '', district: ''});
    setProvinces([]);
    setDistricts([]);
    if (department) {
      try {
        const response = await api.get(`/locations/provinces/${department}`);
        setProvinces(response.data || []);
      } catch (error) {
        console.error('Error loading provinces:', error);
      }
    }
  };

  const handleProvinceChange = async (province: string) => {
    setClientForm({...clientForm, province, district: ''});
    setDistricts([]);
    if (province && clientForm.department) {
      try {
        const response = await api.get(`/locations/districts/${clientForm.department}/${province}`);
        setDistricts(response.data || []);
      } catch (error) {
        console.error('Error loading districts:', error);
      }
    }
  };

  // Funciones para manejar el modal de cliente
  const openClientModal = async () => {
    // Determinar el tipo de documento según el segmento seleccionado
    const segmentInfo = segments.find(s => s.code === form.segment);
    const defaultDocumentType = segmentInfo?.document_type || 'DNI';
    
    let clientData = {
      document_type: defaultDocumentType,
      dni_ruc: form.customerId || '',
      name: form.customerName || '',
      legal_representative: '',
      phone: '',
      address: '',
      department: '',
      province: '',
      district: ''
    };

    // Si ya hay un DNI/RUC, intentar cargar los datos del cliente
    if (form.customerId && form.customerId.length >= 8) {
      try {
        const response = await api.get(`/clients/${form.customerId}`);
        if (response.data) {
          clientData = {
            document_type: response.data.document_type || 'DNI',
            dni_ruc: response.data.dni_ruc || form.customerId,
            name: response.data.name || form.customerName || '',
            legal_representative: response.data.legal_representative || '',
            phone: response.data.phone || '',
            address: response.data.address || '',
            department: response.data.department || '',
            province: response.data.province || '',
            district: response.data.district || ''
          };
        }
      } catch (error) {
        console.log('Cliente no encontrado, usando datos del formulario');
      }
    }

    setClientForm(clientData);
    setShowClientModal(true);
  };

  const saveClient = async () => {
    // Validaciones obligatorias
    if (!clientForm.dni_ruc) {
      alert('El documento es obligatorio');
      return;
    }
    
    if (!validateDocument(clientForm.document_type, clientForm.dni_ruc)) {
      alert(`El ${clientForm.document_type} debe tener ${clientForm.document_type === 'DNI' ? '8' : '11'} dígitos numéricos`);
      return;
    }
    
    if (!clientForm.department || !clientForm.province || !clientForm.district) {
      alert('Debe seleccionar Departamento, Provincia y Distrito');
      return;
    }
    
    if (!clientForm.name.trim()) {
      alert('El nombre/razón social es obligatorio');
      return;
    }

    try {
      // Verificar existencia para evitar un 404 innecesario
      let exists = false;
      try {
        const check = await api.get(`/clients/${clientForm.dni_ruc}`);
        exists = !!check.data;
      } catch (_) { exists = false; }

      if (exists) {
        await api.put(`/clients/${clientForm.dni_ruc}`, clientForm);
      } else {
        await api.post('/clients', clientForm);
      }

      setShowClientModal(false);
      // Actualizar los campos del formulario principal
      setForm({...form, customerId: clientForm.dni_ruc, customerName: clientForm.name});
      alert('Cliente guardado exitosamente');
    } catch (error) {
      console.error('Error saving client:', error);
      alert('Error al guardar el cliente');
    }
  };

  // Funciones para edición de ventas
  const openEditModal = (sale: any) => {
    // Validar que la venta no esté en estado COMPLETADO
    if (sale.status === 'COMPLETADO') {
      alert('No se puede editar una venta con estado COMPLETADO');
      return;
    }
    
    setEditingSale(sale);
    const currentProduct = products.find(p => p.sku === sale.product_sku);
    setEditForm({
      productSku: sale.product_sku || '',
      status: sale.status || '',
      fe: sale.fe || '',
      orderCode: sale.order_code || '',
      serviceId: sale.service_id || '',
      financialAccount: sale.financial_account || '',
      cantidad: sale.cantidad || 1
    });
    setProductSearchEdit(currentProduct ? `${currentProduct.name} - ${currentProduct.sku}` : sale.product_sku || '');
    setShowEditModal(true);
  };

  const saveSaleEdit = async () => {
    if (!editingSale) return;

    // Validaciones
    if (!editForm.productSku) {
      alert('Por favor seleccione un producto');
      return;
    }
    if (!editForm.status) {
      alert('Por favor seleccione un estado');
      return;
    }
    if (!editForm.cantidad || editForm.cantidad < 1) {
      alert('Por favor ingrese una cantidad válida');
      return;
    }

    try {
      const response = await api.patch(`/sales/${editingSale.id}`, editForm);
      if (response.data) {
        alert('Venta actualizada exitosamente');
        setShowEditModal(false);
        setEditingSale(null);
        setProductSearchEdit('');
        // Recargar las ventas
        const { data } = await api.get('/sales', { params: { } });
        setRows(data || []);
      }
    } catch (error: any) {
      console.error('Error al actualizar venta:', error);
      alert(error.response?.data?.message || 'Error al actualizar la venta');
    }
  };

  // Función para abrir modal de logs
  const openLogsModal = async (sale: any) => {
    setSelectedSaleForLogs(sale);
    setShowLogsModal(true);
    setLoadingLogs(true);
    setSaleLogs([]);
    
    try {
      const response = await api.get(`/sales/${sale.id}/change-logs`);
      setSaleLogs(response.data || []);
    } catch (error: any) {
      console.error('Error al cargar logs:', error);
      alert(error.response?.data?.message || 'Error al cargar los logs de cambios');
      setSaleLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  };

  // Función para obtener el nombre legible del campo
  const getFieldLabel = (fieldName: string) => {
    const labels: { [key: string]: string } = {
      'product_sku': 'Producto',
      'status': 'Estado',
      'cantidad': 'Cantidad',
      'fe': 'FE',
      'order_code': 'Order Code',
      'service_id': 'Service ID',
      'financial_account': 'Cuenta Financiera'
    };
    return labels[fieldName] || fieldName;
  };

  // Función para validar documento
  const validateDocument = (documentType: string, documentNumber: string) => {
    if (documentType === 'DNI') {
      return /^[0-9]{8}$/.test(documentNumber);
    } else if (documentType === 'RUC') {
      return /^[0-9]{11}$/.test(documentNumber);
    }
    return false;
  };

  // Función para buscar datos del documento
  const handleSearchDocument = async () => {
    if (!clientForm.dni_ruc || !validateDocument(clientForm.document_type, clientForm.dni_ruc)) {
      alert(`Por favor ingrese un ${clientForm.document_type} válido`);
      return;
    }

    try {
      const response = await api.get(`/locations/document?documentType=${clientForm.document_type}&documentNumber=${clientForm.dni_ruc}`);
      if (response.data && response.data.name) {
        setClientForm((prev: any) => ({
          ...prev,
          name: response.data.name,
          address: response.data.address || prev.address
        }));
        alert('Datos encontrados y cargados automáticamente');
      } else {
        alert('No se encontraron datos para este documento');
      }
    } catch (error) {
      console.error('Error al buscar documento:', error);
      alert('Error al buscar los datos del documento');
    }
  };

  // Función para buscar cliente automáticamente
  const searchClient = async (dniRuc: string) => {
    if (dniRuc && validateDocument(clientForm.document_type, dniRuc)) {
      try {
        // Buscar en nuestra base de datos
        const response = await api.get(`/clients/${dniRuc}`);
        if (response.data) {
          setForm({...form, customerId: dniRuc, customerName: response.data.name});
        } else {
          setForm({...form, customerId: dniRuc, customerName: ''});
        }
      } catch (error) {
        // Cliente no encontrado
        setForm({...form, customerId: dniRuc, customerName: ''});
      }
    } else {
      setForm({...form, customerId: dniRuc, customerName: ''});
    }
  };

  // Funciones de paginado
  const totalPages = Math.ceil(filteredRows.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentRows = filteredRows.slice(startIndex, endIndex);


  // Función de exportación a Excel
  const exportToExcel = () => {
    const headers = [
      'N°', 'fecha', 'Mes', 'Año', 'SUPERVISOR', 'Ingreso la venta (BACK)', 
      'Tipo de ingreso de la venta', 'Origen', 'EECC', 'Tipo de Ingreso', 'SEGMENTO', 
      'PRODUCTO PLAN', 'appt, date/ESTADO', 'RUC', 'EMPRESA', 'Nombre RRLL', 
      'DNI', 'Fono', 'producto', 'RB', 'RB SIN IGV', 'Cantidad', 'direccion', 'Distrito', 
      'Provincia', 'Departamento', 'usuario', 'FE', 'Codigo de orden', 
      'ID de servicio', 'cta financiera'
    ];
    
    const data = filteredRows.map((row, index) => {
      const date = new Date(row.date);
      const rbSinIgv = Number(row.amount) * 0.82; // RB - 18%
      
      return [
        index + 1, // N°
        date.toLocaleDateString(), // fecha
        date.toLocaleDateString('es', { month: 'long' }), // Mes
        date.getFullYear(), // Año
        '', // SUPERVISOR (necesita relación con equipo)
        row.created_by || '', // Ingreso la venta (BACK)
        row.income_type || '', // Tipo de ingreso de la venta
        row.origin || '', // Origen
        row.seller_email || '', // EECC (nombre del vendedor)
        row.customer_id?.length === 11 ? 'RUC' : 'DNI', // Tipo de Ingreso
        row.segment || '', // SEGMENTO
        '', // PRODUCTO PLAN (necesita nombre del producto)
        row.status || '', // appt, date/ESTADO
        row.customer_id || '', // RUC
        row.customer_name || '', // EMPRESA
        '', // Nombre RRLL (falta en BD)
        row.customer_id || '', // DNI
        '', // Fono (falta en BD)
        row.product_sku || '', // producto
        Number(row.amount), // RB
        rbSinIgv, // RB SIN IGV
        row.cantidad || 1, // Cantidad
        '', // direccion (falta en BD)
        '', // Distrito (falta en BD)
        '', // Provincia (falta en BD)
        '', // Departamento (falta en BD)
        row.system_user || '', // usuario
        row.fe || '', // FE
        row.order_code || '', // Codigo de orden
        row.service_id || '', // ID de servicio
        row.financial_account || '' // cta financiera
      ];
    });

    // Crear workbook y worksheet
    const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Ventas');

    // Configurar anchos de columna
    const colWidths = [
      { wch: 12 }, // Fecha
      { wch: 20 }, // Cliente
      { wch: 15 }, // DNI/RUC
      { wch: 25 }, // Vendedor
      { wch: 8 },  // Segmento
      { wch: 15 }, // Producto
      { wch: 12 }, // Monto
      { wch: 12 }, // Estado
      { wch: 10 }, // FE
      { wch: 15 }, // Order Code
      { wch: 15 }, // Service ID
      { wch: 20 }  // Cuenta Financiera
    ];
    ws['!cols'] = colWidths;

    // Generar y descargar archivo
    const fileName = `ventas_${selectedYear}_${selectedMonth.toString().padStart(2, '0')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  return (
    <div className="container">
      <h2 className="mb-3">Ventas</h2>
      <div className="card mb-3"><div className="card-body">
        <div className="mb-3">
          <small className="text-muted">Los campos marcados con <span className="text-danger">*</span> son obligatorios</small>
        </div>
        <form className="row g-2" onSubmit={submit}>
          {/* 1. Segmento */}
          <div className="col-6 col-md-2">
            <label className="form-label">Segmento</label>
            <select className="form-select" value={form.segment} onChange={(e)=>{
              setForm({...form, segment:e.target.value, teamId: '', sellerEmail: '', sellerName: ''});
              setSellerSearch('');
            }}>
              <option value="B2B">B2B</option>
              <option value="B2C">B2C</option>
            </select>
          </div>
          
          {/* 2. Área */}
          <div className="col-6 col-md-2">
            <label className="form-label">Área <span className="text-danger">*</span></label>
            <select 
              className="form-select" 
              value={form.teamId} 
              onChange={(e) => setForm({...form, teamId: e.target.value, sellerEmail: '', sellerName: ''})}
              disabled={loadingTeams}
              required
            >
              <option value="">{loadingTeams ? 'Cargando...' : 'Seleccione área'}</option>
              {teams.map(team => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </select>
          </div>
          
          {/* 3. Vendedor */}
          <div className="col-12 col-md-4" ref={sellerRef}>
            <label className="form-label">Vendedor <span className="text-danger">*</span></label>
            <div className="position-relative">
              <input 
                type="text" 
                className="form-control" 
                placeholder={loadingSellers ? "Cargando vendedores..." : "Buscar vendedor..."} 
                value={sellerSearch} 
                onChange={(e) => setSellerSearch(e.target.value)}
                onFocus={() => setShowSellerDropdown(true)}
                onClick={() => setShowSellerDropdown(true)}
                disabled={loadingSellers || !form.teamId}
                required
              />
              {showSellerDropdown && !loadingSellers && (
                <div className="position-absolute w-100 bg-white border rounded shadow" style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}>
                  <div 
                    className="p-2 border-bottom cursor-pointer"
                    onClick={() => {
                      setForm({...form, sellerEmail: '', sellerName: ''});
                      setSellerSearch('');
                      setShowSellerDropdown(false);
                    }}
                  >
                    Seleccione...
                  </div>
                  {filteredSellers.map(s => (
                    <div 
                      key={s.email} 
                      className="p-2 cursor-pointer hover-bg-light"
                      onClick={() => {
                        setForm({...form, sellerEmail: s.email, sellerName: s.name});
                        setSellerSearch(s.name);
                        setShowSellerDropdown(false);
                      }}
                    >
                      {s.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* 3. Producto */}
          <div className="col-12 col-md-4">
            <label className="form-label">Producto <span className="text-danger">*</span></label>
            <div className="position-relative" ref={productRef}>
              <input 
                type="text" 
                className="form-control" 
                placeholder="Buscar producto..." 
                value={productSearch} 
                onChange={(e) => setProductSearch(e.target.value)}
                onFocus={() => setShowProductDropdown(true)}
                onClick={() => setShowProductDropdown(true)}
              />
              {showProductDropdown && (
                <div className="position-absolute w-100 bg-white border rounded shadow" style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}>
                  <div 
                    className="p-2 border-bottom cursor-pointer"
                    onClick={() => {
                      setForm({...form, productSku: null});
                      setProductSearch('');
                      setShowProductDropdown(false);
                    }}
                  >
                    Seleccione...
                  </div>
                  {filteredProducts.map(p => (
                    <div 
                      key={p.sku} 
                      className="p-2 cursor-pointer hover-bg-light"
                      onClick={() => {
                        setForm({...form, productSku: p.sku});
                        setProductSearch(p.name);
                        setShowProductDropdown(false);
                      }}
                    >
                      {p.name} - {p.sku}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* 4. Monto */}
          <div className="col-6 col-md-2">
            <label className="form-label">Monto <span className="text-danger">*</span></label>
            <input className="form-control" type="number" value={form.amount||''} onChange={(e)=>setForm({...form, amount:e.target.value})} />
          </div>
          
          {/* 5. Cantidad */}
          <div className="col-6 col-md-2">
            <label className="form-label">Cantidad <span className="text-danger">*</span></label>
            <input className="form-control" type="number" min="1" value={form.cantidad||1} onChange={(e)=>setForm({...form, cantidad:e.target.value})} />
          </div>
          
          {/* 6. Cliente DNI/RUC */}
          <div className="col-6 col-md-3">
            <label className="form-label">{getDocumentText().label} <span className="text-danger">*</span></label>
            <div className="input-group">
              <input 
                className="form-control" 
                value={form.customerId||''} 
                onChange={(e)=>searchClient(e.target.value)} 
                placeholder={getDocumentText().placeholder}
              />
              <button type="button" className="btn btn-outline-secondary" onClick={openClientModal}>
                📝
              </button>
            </div>
          </div>
          
          {/* 7. Nombre/Razón Social */}
          <div className="col-12 col-md-4">
            <label className="form-label">{getDocumentText().nameLabel}</label>
            <div className="form-control-plaintext bg-light border rounded px-3 py-2">
              {form.customerName || 'No registrado'}
            </div>
          </div>
          
          {/* 8. Estado */}
          <div className="col-6 col-md-2">
            <label className="form-label">Estado</label>
            <select className="form-select" value={form.status} onChange={(e)=>setForm({...form, status:e.target.value})}>
              {['ASIGNADO','ENTREGADO','AGENDADO','COMPLETADO','RECHAZADO','EN_PROCESO'].map(s=>(<option key={s} value={s}>{s}</option>))}
            </select>
          </div>
          
          {/* 9. Tipo de ingreso de la venta */}
          <div className="col-6 col-md-2">
            <label className="form-label">Tipo de ingreso</label>
            <select className="form-select" value={form.incomeType} onChange={(e)=>setForm({...form, incomeType:e.target.value})}>
              <option value="AGR">AGR</option>
              <option value="Remoto">Remoto</option>
              <option value="Express">Express</option>
            </select>
          </div>
          
          {/* 10. Origen */}
          <div className="col-6 col-md-2">
            <label className="form-label">Origen <span className="text-danger">*</span></label>
            <select className="form-select" value={form.origin} onChange={(e)=>setForm({...form, origin:e.target.value})}>
              <option value="Postpago">Postpago</option>
              <option value="Prepago">Prepago</option>
              <option value="Alta">Alta</option>
            </select>
          </div>
          
          {/* 11. Usuario del sistema */}
          <div className="col-12 col-md-4">
            <label className="form-label">Usuario del sistema</label>
            <input 
              className="form-control" 
              placeholder="Username usado en el sistema de ventas del producto" 
              value={form.systemUser||''} 
              onChange={(e)=>setForm({...form, systemUser:e.target.value})} 
            />
          </div>
          
          {/* 12. Campos adicionales */}
          <div className="col-12">
            <div className="row g-2">
              <div className="col-6 col-md-3">
                <label className="form-label">FE</label>
                <input className="form-control" placeholder="FE" value={form.fe||''} onChange={(e)=>setForm({...form, fe:e.target.value})} />
              </div>
              <div className="col-6 col-md-3">
                <label className="form-label">Order Code</label>
                <input className="form-control" placeholder="Order Code" value={form.orderCode||''} onChange={(e)=>setForm({...form, orderCode:e.target.value})} />
              </div>
              <div className="col-6 col-md-3">
                <label className="form-label">Service ID</label>
                <input className="form-control" placeholder="Service ID" value={form.serviceId||''} onChange={(e)=>setForm({...form, serviceId:e.target.value})} />
          </div>
          <div className="col-6 col-md-3">
                <label className="form-label">Cuenta Financiera</label>
                <input className="form-control" placeholder="Cuenta Financiera" value={form.financialAccount||''} onChange={(e)=>setForm({...form, financialAccount:e.target.value})} />
              </div>
            </div>
          </div>
          <div className="col-12"><button className="btn btn-primary" disabled={loading}>{loading ? 'Guardando...' : 'Guardar'}</button></div>
        </form>
      </div></div>

      <div className="card"><div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="card-title mb-0">Ventas recientes</h5>
          <button className="btn btn-success btn-sm" onClick={exportToExcel}>
            📊 Exportar XLSX
          </button>
        </div>
        
        {/* Controles de filtro */}
        <div className="row g-2 mb-3">
          <div className="col-md-2">
            <label className="form-label">Año</label>
            <select 
              className="form-select form-select-sm" 
              value={selectedYear} 
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label">Mes</label>
            <select 
              className="form-select form-select-sm" 
              value={selectedMonth} 
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
            >
              {Array.from({length: 12}, (_, i) => (
                <option key={i+1} value={i+1}>
                  {new Date(0, i).toLocaleString('es', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label">Buscar</label>
            <input 
              type="text" 
              className="form-control form-control-sm" 
              placeholder="Cliente, vendedor, producto, estado..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="col-md-2">
            <label className="form-label">Ordenar por</label>
            <select 
              className="form-select form-select-sm" 
              value={sortField} 
              onChange={(e) => setSortField(e.target.value)}
            >
              <option value="date">Fecha</option>
              <option value="customer_name">Cliente</option>
              <option value="seller_email">Vendedor</option>
              <option value="amount">Monto</option>
              <option value="status">Estado</option>
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label">Dirección</label>
            <select 
              className="form-select form-select-sm" 
              value={sortOrder} 
              onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
            >
              <option value="desc">Descendente</option>
              <option value="asc">Ascendente</option>
            </select>
          </div>
        </div>
        
        {/* Información de resultados */}
        <div className="mb-2">
          <small className="text-muted">
            Mostrando {startIndex + 1}-{Math.min(endIndex, filteredRows.length)} de {filteredRows.length} ventas
          </small>
        </div>
        <div className="table-responsive">
          <table className="table table-sm table-hover">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Cliente</th>
                <th>Vendedor</th>
                <th>Producto</th>
                <th>Monto</th>
                <th>Estado</th>
                <th>Detalles</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {currentRows.map((r:any)=>(
                <tr key={r.id}>
                  <td>
                    <div className="fw-bold">{new Date(r.date).toLocaleDateString()}</div>
                    <small className="text-muted">{new Date(r.date).toLocaleTimeString()}</small>
                  </td>
                  <td>
                    <div className="fw-bold">{r.customer_name || 'Sin nombre'}</div>
                    <small className="text-muted">{r.customer_id || 'Sin DNI/RUC'}</small>
                  </td>
                  <td>
                    <div className="fw-bold">{r.seller_email}</div>
                    <small className="text-muted">{r.segment}</small>
                  </td>
                  <td>
                    <div className="fw-bold">{r.product_sku}</div>
                    <small className="text-muted">SKU</small>
                  </td>
                  <td>
                    <div className="fw-bold text-success">S/ {Number(r.amount).toFixed(2)}</div>
                  </td>
                  <td>
                    <span className={`badge ${
                      r.status === 'COMPLETADO' ? 'bg-success' :
                      r.status === 'ASIGNADO' ? 'bg-primary' :
                      r.status === 'EN_PROCESO' ? 'bg-warning' :
                      r.status === 'RECHAZADO' ? 'bg-danger' :
                      'bg-secondary'
                    }`}>
                      {r.status}
                    </span>
                  </td>
                  <td>
                    <div className="d-flex flex-column gap-1">
                      {r.fe && <small className="text-muted"><strong>FE:</strong> {r.fe}</small>}
                      {r.order_code && <small className="text-muted"><strong>Order:</strong> {r.order_code}</small>}
                      {r.service_id && <small className="text-muted"><strong>Service:</strong> {r.service_id}</small>}
                      {r.financial_account && <small className="text-muted"><strong>Cuenta:</strong> {r.financial_account}</small>}
                      {!r.fe && !r.order_code && !r.service_id && !r.financial_account && 
                        <small className="text-muted">Sin detalles</small>
                      }
                    </div>
                  </td>
                  <td>
                    <div className="d-flex gap-1">
                      <button 
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => openEditModal(r)}
                        disabled={r.status === 'COMPLETADO'}
                        title={r.status === 'COMPLETADO' ? 'No se puede editar una venta completada' : 'Editar detalles de la venta'}
                      >
                        ✏️
                      </button>
                      <button 
                        className="btn btn-sm btn-outline-info"
                        onClick={() => openLogsModal(r)}
                        title="Ver historial de cambios"
                      >
                        📋
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Controles de paginado */}
        {totalPages > 1 && (
          <nav aria-label="Paginación de ventas" className="mt-3">
            <ul className="pagination pagination-sm justify-content-center">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button 
                  className="page-link" 
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  Primera
                </button>
              </li>
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button 
                  className="page-link" 
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Anterior
                </button>
              </li>
              
              {/* Páginas numeradas */}
              {Array.from({length: Math.min(5, totalPages)}, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  </li>
                );
              })}
              
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button 
                  className="page-link" 
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Siguiente
                </button>
              </li>
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button 
                  className="page-link" 
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Última
                </button>
              </li>
            </ul>
          </nav>
        )}
      </div></div>

      {/* Modal de Cliente */}
      {showClientModal && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Información del Cliente</h5>
                <button type="button" className="btn-close" onClick={() => setShowClientModal(false)}></button>
              </div>
             <div className="modal-body">
               <div className="row g-3">
                 <div className="col-md-4">
                   <label className="form-label">Tipo de Documento <span className="text-danger">*</span></label>
                   <input 
                     className="form-control" 
                     value={clientForm.document_type} 
                     readOnly
                     style={{backgroundColor: '#f8f9fa', cursor: 'not-allowed'}}
                     title={`Tipo de documento determinado por el segmento seleccionado (${form.segment} = ${clientForm.document_type})`}
                   />
                 </div>
                 <div className="col-md-8">
                   <label className="form-label">{clientForm.document_type} <span className="text-danger">*</span></label>
                   <div className="input-group">
                     <input 
                       className="form-control" 
                       value={clientForm.dni_ruc} 
                       onChange={(e) => {
                         const value = e.target.value.replace(/\D/g, ''); // Solo números
                         const maxLength = clientForm.document_type === 'DNI' ? 8 : 11;
                         if (value.length <= maxLength) {
                           setClientForm({...clientForm, dni_ruc: value});
                         }
                       }} 
                       placeholder={`Ingrese ${clientForm.document_type === 'DNI' ? '8' : '11'} dígitos`}
                       maxLength={clientForm.document_type === 'DNI' ? 8 : 11}
                       title={`${clientForm.document_type === 'DNI' ? 'DNI debe tener exactamente 8 dígitos' : 'RUC debe tener exactamente 11 dígitos'}`}
                     />
                     <button 
                       type="button" 
                       className="btn btn-outline-primary" 
                       onClick={handleSearchDocument}
                       disabled={!clientForm.dni_ruc || !validateDocument(clientForm.document_type, clientForm.dni_ruc)}
                       title="Buscar datos del documento"
                     >
                       🔍 Buscar
                     </button>
                   </div>
                 </div>
                  <div className="col-md-6">
                    <label className="form-label">{getDocumentText().nameLabel} <span className="text-danger">*</span></label>
                    <input 
                      className="form-control" 
                      value={clientForm.name} 
                      onChange={(e) => setClientForm({...clientForm, name: e.target.value})} 
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Representante Legal</label>
                    <input 
                      className="form-control" 
                      value={clientForm.legal_representative} 
                      onChange={(e) => setClientForm({...clientForm, legal_representative: e.target.value})} 
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Teléfono</label>
                    <input 
                      className="form-control" 
                      value={clientForm.phone} 
                      onChange={(e) => setClientForm({...clientForm, phone: e.target.value})} 
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Dirección</label>
                    <input 
                      className="form-control" 
                      value={clientForm.address} 
                      onChange={(e) => setClientForm({...clientForm, address: e.target.value})} 
                    />
                  </div>
                 <div className="col-md-4">
                   <label className="form-label">Departamento <span className="text-danger">*</span></label>
                   <select 
                     className="form-select" 
                     value={clientForm.department} 
                     onChange={(e) => handleDepartmentChange(e.target.value)}
                   >
                     <option value="">Seleccione...</option>
                     {departments.map(dept => (
                       <option key={dept} value={dept}>{dept}</option>
                     ))}
                   </select>
                 </div>
                 <div className="col-md-4">
                   <label className="form-label">Provincia <span className="text-danger">*</span></label>
                   <select 
                     className="form-select" 
                     value={clientForm.province} 
                     onChange={(e) => handleProvinceChange(e.target.value)}
                     disabled={!clientForm.department}
                   >
                     <option value="">Seleccione...</option>
                     {provinces.map(prov => (
                       <option key={prov} value={prov}>{prov}</option>
                     ))}
                   </select>
                 </div>
                 <div className="col-md-4">
                   <label className="form-label">Distrito <span className="text-danger">*</span></label>
                   <select 
                     className="form-select" 
                     value={clientForm.district} 
                     onChange={(e) => setClientForm({...clientForm, district: e.target.value})}
                     disabled={!clientForm.province}
                   >
                     <option value="">Seleccione...</option>
                     {districts.map(dist => (
                       <option key={dist} value={dist}>{dist}</option>
                     ))}
                   </select>
                 </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowClientModal(false)}>
                  Cancelar
                </button>
                <button type="button" className="btn btn-primary" onClick={saveClient}>
                  Guardar Cliente
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de logs de cambios */}
      {showLogsModal && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Historial de Cambios - Venta #{selectedSaleForLogs?.id?.substring(0, 8)}</h5>
                <button type="button" className="btn-close" onClick={() => {
                  setShowLogsModal(false);
                  setSelectedSaleForLogs(null);
                  setSaleLogs([]);
                }}></button>
              </div>
              <div className="modal-body">
                {loadingLogs ? (
                  <div className="text-center py-4">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Cargando...</span>
                    </div>
                    <p className="mt-2">Cargando historial de cambios...</p>
                  </div>
                ) : saleLogs.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-muted">No hay cambios registrados para esta venta.</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Usuario</th>
                          <th>Campo</th>
                          <th>Valor Anterior</th>
                          <th>Valor Nuevo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {saleLogs.map((log: any) => (
                          <tr key={log.id}>
                            <td>
                              <small>
                                {new Date(log.changed_at).toLocaleDateString('es-PE')}<br/>
                                <span className="text-muted">{new Date(log.changed_at).toLocaleTimeString('es-PE')}</span>
                              </small>
                            </td>
                            <td>
                              <div className="fw-bold">{log.changed_by_name || log.changed_by}</div>
                              <small className="text-muted">{log.changed_by}</small>
                            </td>
                            <td>
                              <span className="badge bg-secondary">{getFieldLabel(log.field_name)}</span>
                            </td>
                            <td>
                              <span className="text-danger">
                                {log.old_value || <em className="text-muted">(vacío)</em>}
                              </span>
                            </td>
                            <td>
                              <span className="text-success fw-bold">
                                {log.new_value || <em className="text-muted">(vacío)</em>}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => {
                  setShowLogsModal(false);
                  setSelectedSaleForLogs(null);
                  setSaleLogs([]);
                }}>
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de edición de venta */}
      {showEditModal && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Editar Detalles de Venta</h5>
                <button type="button" className="btn-close" onClick={() => setShowEditModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label">Producto <span className="text-danger">*</span></label>
                    <div className="position-relative" ref={productEditRef}>
                      <input 
                        type="text" 
                        className="form-control" 
                        placeholder="Buscar producto..." 
                        value={productSearchEdit} 
                        onChange={(e) => setProductSearchEdit(e.target.value)}
                        onFocus={() => setShowProductDropdownEdit(true)}
                        onClick={() => setShowProductDropdownEdit(true)}
                      />
                      {showProductDropdownEdit && (
                        <div className="position-absolute w-100 bg-white border rounded shadow" style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}>
                          <div 
                            className="p-2 border-bottom cursor-pointer"
                            onClick={() => {
                              setEditForm({...editForm, productSku: ''});
                              setProductSearchEdit('');
                              setShowProductDropdownEdit(false);
                            }}
                          >
                            Seleccione...
                          </div>
                          {products
                            .filter(p => 
                              p.name.toLowerCase().includes(productSearchEdit.toLowerCase()) || 
                              p.sku.toLowerCase().includes(productSearchEdit.toLowerCase())
                            )
                            .map(p => (
                              <div 
                                key={p.sku} 
                                className="p-2 cursor-pointer hover-bg-light"
                                onClick={() => {
                                  setEditForm({...editForm, productSku: p.sku});
                                  setProductSearchEdit(`${p.name} - ${p.sku}`);
                                  setShowProductDropdownEdit(false);
                                }}
                              >
                                {p.name} - {p.sku}
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Estado <span className="text-danger">*</span></label>
                    <select 
                      className="form-select" 
                      value={editForm.status} 
                      onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                    >
                      <option value="">Seleccione...</option>
                      <option value="ASIGNADO">ASIGNADO</option>
                      <option value="ENTREGADO">ENTREGADO</option>
                      <option value="AGENDADO">AGENDADO</option>
                      <option value="COMPLETADO">COMPLETADO</option>
                      <option value="RECHAZADO">RECHAZADO</option>
                      <option value="EN_PROCESO">EN_PROCESO</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Cantidad <span className="text-danger">*</span></label>
                    <input 
                      className="form-control" 
                      type="number" 
                      min="1" 
                      value={editForm.cantidad} 
                      onChange={(e) => setEditForm({...editForm, cantidad: Number(e.target.value)})}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">FE</label>
                    <input 
                      className="form-control" 
                      value={editForm.fe} 
                      onChange={(e) => setEditForm({...editForm, fe: e.target.value})}
                      placeholder="Número de FE"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Order Code</label>
                    <input 
                      className="form-control" 
                      value={editForm.orderCode} 
                      onChange={(e) => setEditForm({...editForm, orderCode: e.target.value})}
                      placeholder="Código de orden"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Service ID</label>
                    <input 
                      className="form-control" 
                      value={editForm.serviceId} 
                      onChange={(e) => setEditForm({...editForm, serviceId: e.target.value})}
                      placeholder="ID del servicio"
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Cuenta Financiera</label>
                    <input 
                      className="form-control" 
                      value={editForm.financialAccount} 
                      onChange={(e) => setEditForm({...editForm, financialAccount: e.target.value})}
                      placeholder="Cuenta financiera"
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancelar
                </button>
                <button type="button" className="btn btn-primary" onClick={saveSaleEdit}>
                  Guardar Cambios
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sales;
